import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle2,
  FileText,
  Shield,
  Briefcase,
  ExternalLink,
  Filter
} from 'lucide-react';

function DeadlineControl({ user }) {
  const navigate = useNavigate();
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDeadlines();
  }, []);

  const fetchDeadlines = async () => {
    try {
      const response = await api.get('/deadline-control');
      setDeadlines(response.data);
    } catch (error) {
      console.error('Error fetching deadlines:', error);
      toast.error('Erro ao carregar prazos');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status, daysUntil) => {
    if (status === 'expired') {
      return <Badge className="bg-red-100 text-red-700 border-red-200">Vencido</Badge>;
    } else if (status === 'expiring_soon') {
      return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Vence em {daysUntil} dias</Badge>;
    } else {
      return <Badge className="bg-green-100 text-green-700 border-green-200">Válido</Badge>;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'license':
        return <Shield className="w-5 h-5 text-blue-600" />;
      case 'art':
        return <Briefcase className="w-5 h-5 text-purple-600" />;
      case 'protocol':
        return <FileText className="w-5 h-5 text-orange-600" />;
      default:
        return <Calendar className="w-5 h-5 text-gray-600" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'license':
        return 'Licença';
      case 'art':
        return 'ART';
      case 'protocol':
        return 'Protocolo';
      default:
        return type;
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'license':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'art':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'protocol':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  // Filter deadlines
  const filteredDeadlines = deadlines.filter(d => {
    // Type filter
    if (activeTab !== 'all' && d.type !== activeTab) return false;
    
    // Status filter
    if (statusFilter !== 'all' && d.status !== statusFilter) return false;
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        d.name.toLowerCase().includes(search) ||
        d.project_name.toLowerCase().includes(search) ||
        d.client_name.toLowerCase().includes(search) ||
        (d.number && d.number.toLowerCase().includes(search))
      );
    }
    
    return true;
  });

  // Stats
  const stats = {
    total: deadlines.length,
    expired: deadlines.filter(d => d.status === 'expired').length,
    expiringSoon: deadlines.filter(d => d.status === 'expiring_soon').length,
    valid: deadlines.filter(d => d.status === 'valid').length,
    licenses: deadlines.filter(d => d.type === 'license').length,
    arts: deadlines.filter(d => d.type === 'art').length,
    protocols: deadlines.filter(d => d.type === 'protocol').length,
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-semibold text-gray-900">Controle de Prazos</h1>
          <p className="text-gray-600 mt-1">Acompanhe vencimentos de licenças, ARTs e protocolos</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card className="p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Calendar className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 border border-red-200 bg-red-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-700">{stats.expired}</p>
                <p className="text-xs text-red-600">Vencidos</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 border border-amber-200 bg-amber-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700">{stats.expiringSoon}</p>
                <p className="text-xs text-amber-600">A Vencer</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 border border-green-200 bg-green-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">{stats.valid}</p>
                <p className="text-xs text-green-600">Válidos</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 border border-blue-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{stats.licenses}</p>
                <p className="text-xs text-blue-600">Licenças</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 border border-purple-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Briefcase className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700">{stats.arts}</p>
                <p className="text-xs text-purple-600">ARTs</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 border border-orange-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <FileText className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-700">{stats.protocols}</p>
                <p className="text-xs text-orange-600">Protocolos</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4 border border-gray-200">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtros:</span>
            </div>
            
            <Input
              placeholder="Buscar por nome, projeto ou cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64"
            />
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Status</SelectItem>
                <SelectItem value="expired">Vencidos</SelectItem>
                <SelectItem value="expiring_soon">A Vencer</SelectItem>
                <SelectItem value="valid">Válidos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Tabs and Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">
              Todos ({stats.total})
            </TabsTrigger>
            <TabsTrigger value="license">
              <Shield className="w-4 h-4 mr-2" />
              Licenças ({stats.licenses})
            </TabsTrigger>
            <TabsTrigger value="art">
              <Briefcase className="w-4 h-4 mr-2" />
              ARTs ({stats.arts})
            </TabsTrigger>
            <TabsTrigger value="protocol">
              <FileText className="w-4 h-4 mr-2" />
              Protocolos ({stats.protocols})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            <Card className="border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome/Número</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Projeto</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vencimento</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredDeadlines.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          Nenhum prazo encontrado com os filtros selecionados
                        </td>
                      </tr>
                    ) : (
                      filteredDeadlines.map((deadline) => (
                        <tr key={`${deadline.type}-${deadline.id}`} className={`hover:bg-gray-50 ${
                          deadline.status === 'expired' ? 'bg-red-50' : 
                          deadline.status === 'expiring_soon' ? 'bg-amber-50' : ''
                        }`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {getTypeIcon(deadline.type)}
                              <Badge className={getTypeBadgeColor(deadline.type)}>
                                {getTypeLabel(deadline.type)}
                              </Badge>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{deadline.name}</p>
                              {deadline.number && (
                                <p className="text-xs text-gray-500">Nº {deadline.number}</p>
                              )}
                              {deadline.activity && (
                                <p className="text-xs text-gray-500">{deadline.activity}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{deadline.project_name}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{deadline.client_name}</td>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-gray-900">
                              {new Date(deadline.expiration_date).toLocaleDateString('pt-BR')}
                            </p>
                            <p className="text-xs text-gray-500">
                              {deadline.days_until < 0 
                                ? `${Math.abs(deadline.days_until)} dias atrás`
                                : deadline.days_until === 0
                                  ? 'Vence hoje'
                                  : `em ${deadline.days_until} dias`
                              }
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            {getStatusBadge(deadline.status, deadline.days_until)}
                          </td>
                          <td className="px-4 py-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/projects/${deadline.project_id}`)}
                              className="text-emerald-600 hover:text-emerald-700"
                            >
                              <ExternalLink className="w-4 h-4 mr-1" />
                              Ver Projeto
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

export default DeadlineControl;
